import { useRouter } from 'next/dist/client/router';
import { memo, PropsWithChildren, useEffect, useState } from 'react';
import { ChevronDown, LucideIcon } from 'lucide-react';
import cn from 'classnames';
import Link from 'next/link';
import Copyright from './Copyright';
import LangCountrySelector from './LangCountrySelector';
import Socials from './Socials';
import SubscribeNewsletter from './SubscribeNewsletter';
import { getFooterElements } from '../constants';

const SM_BREAKPOINT = 640;

const FooterLink = memo(
  ({
    title,
    link,
    target,
  }: {
    title: string;
    link: string;
    target?: string;
  }) => (
    <li>
      <Link
        href={link}
        className="hover:text-teal-500"
        target={target || '_self'}
      >
        {title}
      </Link>
    </li>
  ),
);

const FooterHeading = memo(
  ({
    title,
    isOpen,
    showArrow,
  }: {
    title: string;
    isOpen: boolean;
    showArrow: boolean;
  }) => (
    <h4 className="relative flex w-full items-center justify-between text-left font-black">
      {title}
      {showArrow && (
        <ChevronDown
          className={cn('h-5 w-5 transition-all duration-150 ease-in-out', {
            'rotate-180': isOpen,
          })}
        />
      )}
    </h4>
  ),
);

const FancyFooterLink = memo(
  ({
    title,
    link,
    icon,
  }: {
    title: string;
    link: string;
    icon: LucideIcon;
  }) => {
    const Icon = icon;

    return (
      <li>
        <a
          className="my-1 mr-4 flex h-9 max-w-[50%] items-center justify-between rounded-sm bg-white px-3 text-[10px] text-gray-700 xxs:max-w-[150px]"
          href={link}
        >
          <span className="font-black uppercase !leading-none tracking-wide text-gray-700">
            {title}
          </span>
          <div className="ml-2 w-[18px]">
            <Icon className="h-4 w-4" />
          </div>
        </a>
      </li>
    );
  },
);

const FooterAccordion = memo(
  ({ title, children }: PropsWithChildren<{ title: string }>) => {
    const [isMobile, setIsMobile] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
      setIsMobile(window.innerWidth < SM_BREAKPOINT);
      let timeout: ReturnType<typeof setTimeout>;

      const check = () => {
        clearTimeout(timeout);
        timeout = setTimeout(
          () => setIsMobile(window.innerWidth < SM_BREAKPOINT),
          150,
        );
      };

      window.addEventListener('resize', check);

      return () => {
        window.removeEventListener('resize', check);
        clearTimeout(timeout);
      };
    }, []);

    const expanded = !isMobile || isOpen;

    return (
      <div className="relative flex flex-col gap-2">
        <button
          className="w-full"
          onClick={() => isMobile && setIsOpen((o) => !o)}
          aria-expanded={expanded}
        >
          <FooterHeading title={title} isOpen={isOpen} showArrow={isMobile} />
        </button>
        {expanded && children}
      </div>
    );
  },
);

const ExtendedFooter = () => {
  const router = useRouter();
  // Resolved per render, so the labels pick up the locale `_app` has set.
  const footerElements = getFooterElements();

  return (
    <div
      className={cn('bg-gray-700 text-sm text-white', {
        'pb-20 lg:pb-0': router.pathname.includes('/account/'),
        'pb-[95px] lg:pb-0': router.query.skuId != null,
      })}
    >
      <div className="reebelo-container">
        <div className="flex flex-col gap-10 pb-6 pt-10 sm:grid sm:grid-cols-3 sm:gap-4 sm:pt-16 lg:grid-cols-4">
          {(Object.keys(footerElements) as (keyof typeof footerElements)[]).map(
            (section) => (
              <FooterAccordion
                title={footerElements[section].title}
                key={section}
              >
                <ul
                  className="flex flex-col gap-2"
                  id={`e2e-${section}-footer`}
                >
                  {footerElements[section].links.map((el) =>
                    el.icon ? (
                      <FancyFooterLink
                        title={el.title}
                        link={el.link}
                        icon={el.icon}
                        key={`${section}-${el.title}`}
                      />
                    ) : (
                      <FooterLink
                        title={el.title}
                        link={el.link}
                        key={`${section}-${el.title}`}
                      />
                    ),
                  )}
                </ul>
              </FooterAccordion>
            ),
          )}
          <div className="col-span-2 lg:col-span-1">
            <SubscribeNewsletter />
          </div>
        </div>
        <hr className="mt-6 hidden border-white/20 sm:block" />
        <div className="flex flex-col items-center justify-between gap-6 py-6 sm:flex-row">
          <LangCountrySelector />
          <Socials />
        </div>
      </div>
      <Copyright />
    </div>
  );
};

export default ExtendedFooter;
